/**
 * Apps handler — apps:changed (server → frontend) into the apps store, plus the
 * one seed read after a reload.
 *
 * Level-triggered by design: every apps:changed carries the user's FULL list
 * and the store replaces it wholesale. A garbage frame is ignored so a
 * malformed relay payload never corrupts the list.
 */
import type { Socket } from "socket.io-client";
import { EVENTS, type AppsChangedPayload } from "@bash/shared";
import { useAppsStore } from "../appsStore";
import { listApps } from "@/lib/api/live-preview";

export function attachAppsHandler(socket: Socket): () => void {
  const onChanged = (payload: AppsChangedPayload) => {
    if (!payload || !Array.isArray(payload.apps)) return;
    useAppsStore.getState().replace(payload.apps);
  };
  socket.on(EVENTS.APPS_CHANGED, onChanged);

  // Seed from the rows on attach and again on every reconnect, so push is
  // never the only way back to the truth after a dropped socket. A seed that
  // could not ask (null) writes nothing and leaves `loaded` alone, so the next
  // reconnect asks again. A change that lands while the seed is in flight is
  // newer and wins: the seed result is applied only if nothing replaced the
  // list meanwhile (the store's version says so).
  const seed = () => {
    const before = useAppsStore.getState().version;
    void listApps().then((apps) => {
      if (apps === null) return;
      const store = useAppsStore.getState();
      if (store.version !== before) return;
      store.replace(apps);
    });
  };
  seed();
  socket.on("connect", seed);

  return () => {
    socket.off(EVENTS.APPS_CHANGED, onChanged);
    socket.off("connect", seed);
    // Teardown is sign-out or a new identity: this list belongs to the old one.
    useAppsStore.getState().reset();
  };
}
